import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Highcharts from 'highcharts';
import { IUser } from 'projects/common/src/app/interfaces/user';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { IShift } from 'src/app/modules/core/interfaces/shift';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';
import { ProductionByOperatorService } from './production-by-operator.service';
import { ProductionModalComponent } from '../common/production-modal/production-modal.component';
import { IProductionByOperator, IProductionDetailByMachine } from '../../../interfaces/sp/productionbymachine';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { IMachineType } from 'projects/common/src/app/interfaces/machine-type';
import { InputDateFormatPipe } from '../../../../core/pipes/input-date-format.pipe';
import { firstLetterUpper } from '../../../../../../../projects/common/src/app/services/utils/util.service';
import { Subject, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';

@Component({
  selector: 'app-production-by-operator',
  templateUrl: './production-by-operator.component.html',
  styleUrls: ['./production-by-operator.component.scss'],
})
export class ProductionByOperatorComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  public title: string;
  public legend: string;
  public hasData = false;
  public displayMessage = false;
  public orderBy: any;
  public orderSelect: any;
  public legends: any;
  public inputDate: any;
  public zoneName: string;

  public productionDataOperator: IProductionByOperator[];
  public productionDetailOperator: IProductionDetailByMachine[];

  public selectLevelOpen = false;
  public selectDiagramTypeOpen = false;

  private debouncer: Subject<any> = new Subject<any>();
  private subscription: Subscription;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private languageService: LanguageService,
    private modalService: NgbModal,
    private productionModalService: ProductionByOperatorService,
    private formatDatePipe: InputDateFormatPipe,
    private datePipe: DatePresentationPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  private initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasMachineType: true,
      hasOrderBy: true,
      hasZone: true,
    };
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.subscription = this.debouncer.pipe(throttleTime(2000)).subscribe(({ name, shift, dateInput }) => {
      this.openMOdalProductionByOperator(name, shift, dateInput);
    });
  }

  /**
   * change selected orderBy
   *
   * @event event
   */
  public selectOrderBy(event): void {
    this.orderSelect = event.orderProductionOperatorValue;
    this.cacheService.saveLocal('orderBy', this.orderSelect);
    this.legends = event.orderProductionOperatorText;
    const params = {
      companyId: this.parameterService.parameters.item('user-data').value.company.id,
      shiftId: this.parameterService.parameters.item('shift').value.shiftId,
      inputDate: this.parameterService.parameters.item('date').value,
      machineTypeId: this.parameterService.parameters.item('machine-context').value.machineTypeId,
      zoneId: this.cacheService.session('selectedZone').zoneId,
      orderBy: this.orderSelect,
    };
    this.productionModalService.getProductionByOperator(params).then((data) => {
      this.drawView(data);
    });
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    this.cacheService.clearLocal('orderBy');
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    super.ngOnDestroy();
  }

  /**
   * draw Graphic production by operator
   *
   * @chartOptions chartOptions
   */
  public drawView(chartOptions?: any): void {
    setTimeout(() => {
      this.drawChart(chartOptions);
    }, 300);
  }

  private async drawChart(chartOptions?: any): Promise<any> {
    const currentMachineTypeId =
      (this.parameterService.parameters.item('machine-context').value as IMachineContext).machineTypeId ??
      (JSON.parse(sessionStorage.getItem('machine-context')) as IMachineContext).machineTypeId;

    this.orderBy = await this.productionModalService.getFilter(currentMachineTypeId);
    this.orderSelect = this.orderSelect ? this.orderSelect : this.orderBy[0].orderProductionOperatorValue;
    const tooltipOrderByLabel = this.legends ? this.legends : this.orderBy[0].orderProductionOperatorText;
    const legendLabel = this.assignLegendLabelText(this.legends);
    this.zoneName = this.cacheService.session('selectedZone').zoneName;
    const tooltipOrderBy = this.languageService.message('order by');

    const isTruck = this.data.objCode === 'TRU';
    const isJumbo = this.data.objCode === 'JMB';
    let yAxisText = '';
    if (isTruck) {
      yAxisText = this.languageService.message('loops').toUpperCase();
    } else if (isJumbo) {
      yAxisText = this.languageService.message('drillings_mts').toUpperCase();
    } else {
      yAxisText = this.languageService.message('shelves').toUpperCase();
    }

    if (chartOptions.length) {
      this.hasData = true;
      this.displayMessage = false;

      this.productionDataOperator = chartOptions;

      const shift = this.parameterService.parameters.item('shift').value as IShift;
      const dateInput = this.parameterService.parameters.item('date').value;
      const date = this.parameterService.parameters.item('date').value;
      const nameOperator = this.productionDataOperator.map((n) => `${n.lastName} ${n.firstName}`);
      const heightStick = 400;
      const serie = this.productionDataOperator.map((s) => s.productionQty);
      const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
      const user = this.parameterService.parameters.item('user-data').value;

      let shiftTooltip = '';

      if (shift.name === this.languageService.message('day_detail')) {
        shiftTooltip = this.languageService.message('empty');
      } else {
        shiftTooltip = this.languageService.message('shift_').toUpperCase();
      }

      const { debouncer } = this;

      const dataSerie = {
        dataLabels: {
          enabled: true,
          style: {
            color: '#707070',
            fontSize: '11px',
            fontWeight: 600,
          },
          crop: false,
          overflow: 'none',
        },
        meditionUnit: '',
        type: 'bar',
        data: serie,
        color: 'rgba(34, 217, 131, 0.5)',
        name: legendLabel,
      };
      const series = [dataSerie];
      const data = {
        chart: {
          type: 'bar',
          height: this.productionDataOperator.length > 12 ? 200 + this.productionDataOperator.length * 20 : heightStick,
          plotBorderWidth: null,
          plotShadow: false,
          backgroundColor: '#F8F8F8',
          style: {
            fontFamily: 'Open Sans',
          },
        },
        title: {
          text: '', // chartTitle,
          y: 20,
          style: {
            fontSize: 13,
            fontWeight: '600',
            fontFamily: 'Open Sans',
            color: '#433E3E',
          },
        },
        subtitle: {
          text: `${this.languageService.message('company')}: ${user.company.name},
                  ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
                  ${this.languageService.message('date')}: ${this.datePipe.transform(date)},
                  ${this.languageService.message('shift_')}: ${shift.name}`,
        },
        credits: {
          enabled: false,
        },
        plotOptions: {
          series: {
            borderWidth: 2,
            borderColor: '#22D983',
            point: {
              events: {
                click: (event: any) => {
                  const name = event.point.category;
                  debouncer.next({ name, shift, dateInput });
                },
              },
            },
            events: {
              legendItemClick: (e) => {
                e.preventDefault();
              },
            },
          },
        },
        legend: {
          useHTML: true,
          squareSymbol: false,
          align: 'center',
          verticalAlign: 'top',
          symbolHeight: 0.001,
          symbolWidth: 0.001,
          symbolRadius: 0.001,
          labelFormatter: () => {
            return `<div style=" height: 20px; display: flex;" >
                <svg width="36" height="18" style=" float: left;">
                  <rect width="30" height="12" x="2" y="2"
                  style="fill: #22D98380; stroke-width: 2px !important;
                  stroke: #22D983 !important;"/>
                </svg>
                <span style="font-size: 12px; font-family: Open Sans; font-weight: 500;
                color: #433E3E; float: left;">${legendLabel}</span>
              </div>`;
          },
        },
        tooltip: {
          backgroundColor: 'rgb(51, 51, 51, 1)',
          borderColor: '#333333',
          borderRadius: 2,
          style: {
            color: '#FFFFFF',
          },
          useHTML: true,
          formatter(): string {
            let divSymbol = '';
            divSymbol = `<div style="background-color: #22d983;
                            width: 30px;
                            height: 12px;
                            border: 1px solid #22d983;
                            float: left;
                            margin-top: 2px">
                        </div>`;

            return `<div style="font-size: 10px;
                                color: #FFFFFF;">
                      <strong style="font-weight: 700;"> ${shiftTooltip.toUpperCase()} ${shift && shift.name.toUpperCase()}</strong> <br>
                      <span style="font-weight: 400;">${tooltipOrderBy.toUpperCase()} : ${tooltipOrderByLabel.toUpperCase()} </span> <br>
                      <span style="font-weight: 400;">${this.x} </span> <br>
                      ${divSymbol} &nbsp;
                      <span style="font-weight: 400;">${this.y} ${legendLabel}
            </span>
                    </div>`;
          },
        },
        xAxis: {
          lineWidth: 1,
          lineColor: '#707070',
          title: {
            text: this.languageService.message('operators').toUpperCase(),
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '600',
            },
          },
          categories: nameOperator,
          gridLineWidth: 1,
          labels: {
            useHTML: true,
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '400',
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
        },
        yAxis: [
          {
            title: {
              text: yAxisText,
              style: {
                fontSize: '12px',
                color: '#707070',
                fontWeight: '600',
              },
            },
            labels: {
              style: {
                fontSize: '10px',
                color: '#707070',
                fontWeight: '400',
              },
            },
            lineWidth: 1,
            lineColor: '#707070',
          },
          {
            opposite: true,
            lineWidth: 1,
            lineColor: '#707070',
            title: {
              text: '',
            },
          },
        ],
        series,
      };

      this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
        if (!this.hasData) {
          const newX = chart.plotWidth / 3 + chart.plotLeft;
          const newY = chart.plotHeight / 2 + chart.plotTop;
          chart.renderer
            .text('La solicitud no arrojó resultados', newX, newY)
            .css({
              color: '#4572A7',
              fontSize: '24px',
            })
            .add();
        }
      });
      window.dispatchEvent(new Event('resize'));
    } else {
      this.displayMessage = true;
      this.hasData = false;
    }
  }

  /**
   * Function to assign a legend label text
   *
   * @param label Label to evaluate
   */
  private assignLegendLabelText(label: string): string {
    const isTruck = this.data.objCode === 'TRU';
    const isJumbo = this.data.objCode === 'JMB';
    let loopEfectiveHourText = '';
    let loopsText = '';
    if (isTruck) {
      loopsText = firstLetterUpper(this.languageService.message('transfers'));
      loopEfectiveHourText = this.languageService.message('loops-effective-hour');
    } else if (isJumbo) {
      loopsText = firstLetterUpper(this.languageService.message('drillings_mts'));
      loopEfectiveHourText = this.languageService.message('drillings-effective-hour');
    } else {
      loopsText = firstLetterUpper(this.languageService.message('shelve'));
      loopEfectiveHourText = this.languageService.message('shelves-effective-hour');
    }

    if (label?.trim() === loopEfectiveHourText?.trim()) {
      label = loopEfectiveHourText;
    } else {
      label = loopsText;
    }

    return label;
  }

  /**
   * Open modal Production by Operator
   */
  private async openMOdalProductionByOperator(nameOperator: string, shift: IShift, date: string): Promise<any> {
    const company = (this.parameterService.parameters.item('user-data').value as IUser).company.id;
    const dateValue = String(this.parameterService.parameters.item('date').value);
    const currentMachine = (this.parameterService.parameters.item('machine-context').value as IMachineContext).name;
    const machineCollection = this.parameterService.parameters.item('machine-collection').value as IMachineType[];
    const machine = machineCollection.find((value) => String(value.name).toLowerCase() === String(currentMachine).toLowerCase());
    const machineType = machine.machineTypeId;
    const idOperator = this.productionDataOperator.find(
      (operator) => `${operator.lastName} ${operator.firstName}` === nameOperator,
    ).operatorId;

    const names = this.productionDataOperator.find((nOperator) => nOperator.operatorId === idOperator).firstName;
    const lastNames = this.productionDataOperator.find((nOperator) => nOperator.operatorId === idOperator).lastName;

    const params = {
      operatorId: idOperator,
      inputDate: dateValue,
      machineTypeId: machineType,
      shiftId: +shift.shiftId,
      companyId: company,
      zoneId: this.cacheService.session('selectedZone').zoneId,
    };

    const data = await this.productionModalService.getInformationModal(params);

    if (data) {
      const componentRef = this.modalService.open(ProductionModalComponent, { centered: true, windowClass: 'modal-width' });
      componentRef.componentInstance.title = `${lastNames} ${names}`;
      componentRef.componentInstance.shift = shift;
      componentRef.componentInstance.date = this.formatDatePipe.transform(date);
      componentRef.componentInstance.data = data;
      componentRef.componentInstance.machineObjCode = this.data.objCode;
      componentRef.componentInstance.machineTypeName = machine;
    }
  }

  /**
   * On destroy component
   */
  public onDestroy(): void {
    super.ngOnDestroy();
  }
}
